/**
 * `BulkOperation`/`BulkOperationItem`/`BulkEditableField` read/write shapes — mirrors the backend
 * contract (`backend/.../module/bulk/api/dto/{BulkOperationView,BulkOperationItemView,
 * BulkEditableFieldView,CreateBulkOperationRequest,ApplyBulkOperationRequest}.java`).
 */

/** `bulk_operation.kind` (V23 CHECK). Bulk IMPORT is `module.ingest`'s own territory — never a kind here. */
export const BULK_KINDS = ['update', 'delete'] as const
export type BulkKind = (typeof BULK_KINDS)[number]

export const BULK_KIND_LABEL: Record<BulkKind, string> = {
  update: 'Bulk update',
  delete: 'Bulk delete',
}

/** `bulk_operation.scope` (V23 CHECK). */
export const BULK_SCOPES = ['selection', 'filter'] as const
export type BulkScope = (typeof BULK_SCOPES)[number]

/** `bulk_operation.status` (V23 CHECK) — `preview -> awaiting_approval -> applied|partially_applied|failed`,
 * three distinct sequential transitions, never collapsed (design §4, AC5/AC6). */
export const BULK_STATUSES = [
  'preview',
  'awaiting_approval',
  'applied',
  'partially_applied',
  'rolled_back',
  'failed',
] as const
export type BulkStatus = (typeof BULK_STATUSES)[number]

/** `bulk_operation_item.status` (V23 CHECK). */
export const BULK_ITEM_STATUSES = ['pending', 'applied', 'skipped', 'failed'] as const
export type BulkItemStatus = (typeof BULK_ITEM_STATUSES)[number]

/** `GET /api/v1/bulk-operations`(`/{id}`) row. */
export interface BulkOperationView {
  id: string
  kind: BulkKind | string
  targetEntity: string
  scope: BulkScope | string
  status: BulkStatus | string
  totalCount: number
  appliedCount: number
  skippedCount: number
  failedCount: number
  initiatedBy: string
  approvedBy: string | null
  appliedAt: string | null
  createdAt: string
  updatedAt: string
  version: number
}

/** `GET /api/v1/bulk-operations/{id}/items`(`/preview`) row — one resolved row's diff/outcome. */
export interface BulkOperationItemView {
  id: string
  entityType: string
  entityId: string
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  status: BulkItemStatus | string
  error: string | null
}

/** `GET /api/v1/bulk-editable-fields?entity=...` row — thin additive GET (this story), the
 * "new bulk edit" field picker's allow-list. `guarded` is disclosure-only (see the backend DTO's
 * own javadoc) — it never changes what the UI must send; `requiresReason` alone governs that. */
export interface BulkEditableFieldView {
  entity: string
  field: string
  editable: boolean
  requiresReason: boolean
  guarded: boolean
}

/** `POST /api/v1/bulk-operations` request body. */
export interface CreateBulkOperationRequest {
  kind: BulkKind
  targetEntity: string
  scope: BulkScope
  selection?: string[] | null
  filterExpr?: string | null
  fieldChanges?: Record<string, unknown>
}

/** `POST /api/v1/bulk-operations/{id}/apply`'s `202` body shape (AC24) — issued instead of the
 * synchronous `BulkOperationView` whenever the batch exceeds `bulk.sync.max_rows`. */
export interface BulkApplyAsyncView {
  jobId: string
}

/** True for the `202 {jobId}` async-dispatch shape, false for the synchronous `BulkOperationView`. */
export function isBulkApplyAsync(
  outcome: BulkOperationView | BulkApplyAsyncView,
): outcome is BulkApplyAsyncView {
  return typeof (outcome as BulkApplyAsyncView).jobId === 'string' && !('status' in outcome)
}
